import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, Panel } from "@/components/shared";
import AgentRunner from "@/components/AgentRunner";
import { api, ago, label } from "@/lib/api";

const HINTS = {
  resume_optimizer: "e.g. Tighten my resume for VP Operations roles in manufacturing",
  cover_letter: "Select a job ID below, or describe the role",
  match_analyst: "Paste a job ID below for a specific analysis",
  interview_coach: "e.g. Panel interview with the CFO next week",
  salary_negotiator: "e.g. Offer is $240k base + 20% bonus; I want $275k",
  career_strategist: "e.g. Move from Director of Finance to CFO within 2 years",
  linkedin_optimizer: "Paste your current headline and About section",
  application_assistant: "Paste the application questions you need answered",
};

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [sel, setSel] = useState("resume_optimizer");
  const [quota, setQuota] = useState(null);
  const [jobId, setJobId] = useState("");
  const [runs, setRuns] = useState([]);
  const [aiDown, setAiDown] = useState(false);
  const load = () => { api.get("/ai/quota").then((r) => setQuota(r.data)); api.get("/ai/runs").then((r) => setRuns(r.data)); };
  useEffect(() => {
    api.get("/ai/agents").then((r) => setAgents(r.data));
    api.get("/status").then((r) => setAiDown(r.data.components.some((c) => c.name === "JobPedestal Intelligence" && !c.ok)));
    load();
  }, []);
  const agent = agents.find((a) => a.key === sel);
  return (
    <div>
      <PageHeader eyebrow="Your AI career team" title="JobPedestal Intelligence">
        {quota && <span className="rounded-full border bg-white px-4 py-2 font-mono text-sm" data-testid="agents-quota">{quota.credits_used}/{quota.credits_limit} credits · {quota.plan_name}</span>}
      </PageHeader>
      {aiDown && <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="ai-unavailable-banner">JobPedestal Intelligence is temporarily unavailable. Requests may fail until service is restored, and no credits are charged for failed requests.</div>}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <nav className="space-y-1" aria-label="Agents">
          {agents.map((a) => (
            <button key={a.key} onClick={() => setSel(a.key)} data-testid={`agent-tab-${a.key}`}
              className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm font-medium transition-colors ${sel === a.key ? "bg-[hsl(var(--obsidian))] text-white" : "hover:bg-white"}`}>
              <span className="flex items-center gap-2"><Sparkles className="h-4 w-4" />{a.name}</span><span className="font-mono text-xs opacity-70">{a.credits}cr</span>
            </button>
          ))}
        </nav>
        <div className="space-y-6">
          {agent && (
            <Panel testid="agent-workspace">
              <h2 className="text-xl font-semibold">{agent.name}</h2>
              <p className="mb-5 mt-1 text-sm text-muted-foreground">Uses your saved profile and resume. Costs {agent.credits} credit(s) per run; identical repeat requests are served from cache at no charge.</p>
              <Input className="mb-3" placeholder="Optional job ID (from a job page URL) for job-specific output" value={jobId} onChange={(e) => setJobId(e.target.value.trim())} data-testid="agent-job-id-input" />
              <AgentRunner key={sel} agent={sel} jobId={jobId} placeholder={HINTS[sel]} onResult={load} />
            </Panel>
          )}
          <Panel testid="agent-history"><h3 className="mb-3 font-semibold">Recent runs</h3>
            {runs.length === 0 ? <p className="text-sm text-muted-foreground">No runs yet.</p> : runs.slice(0, 8).map((r) => (
              <details key={r.run_id} className="border-b py-2 text-sm last:border-0"><summary className="cursor-pointer">{label(r.agent)} · {ago(r.created_at)}{r.cached && " · cached"}</summary><p className="mt-2 whitespace-pre-wrap text-muted-foreground">{r.output}</p></details>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
}
